import EventEmitter from 'events';

import {
    createGeneralDataSyncDecoder,
    type DataSyncDecoderFn,
    type DataSyncDecoderHandlers,
    dataSyncProtocol,
    getSubscriptionId,
} from '@gitbook/hive-core';
import type { MaybePromise } from '@gitbook/util-ts';
import {
    WebSocket,
    type WebSocketMessage,
    type WebSocketOptions,
    WebSocketState,
} from '@gitbook/util-websocket';

import { HiveV2Socket } from './HiveV2Socket';

/** Per-WebSocket state tracked by the manager */
interface WebSocketData {
    /** The contentId this WebSocket is associated with */
    contentId: string;
    /** All HiveV2Socket instances sharing this WebSocket */
    sockets: Set<HiveV2Socket>;
    /** Timer for the next scheduled auto-reconnect attempt */
    reconnectTimer?: NodeJS.Timeout;
    /** Current exponential-backoff delay for auto-reconnect (ms). Doubles on each attempt, capped at 30 s. */
    reconnectBackoffDelay: number;
    /** The original WebSocket options supplied by the consumer (e.g. custom wsImpl). Preserved across replacements. */
    webSocketOptions?: WebSocketOptions;
}

export class HiveDataSyncV2Manager {
    private websockets: Map<string, WebSocket> = new Map();

    /** All per-WebSocket state in a single WeakMap */
    private websocketData: WeakMap<WebSocket, WebSocketData> = new WeakMap();

    /** Track ongoing reconnection promises per contentId */
    private reconnectionPromises: Map<string, Promise<void>> = new Map();

    /** Track ongoing open promises per contentId to prevent duplicate opens */
    private openPromises: Map<string, Promise<void>> = new Map();

    /** Next subscription ID to assign */
    private nextSubscriptionId = 1;

    /** Promise chain that serializes subscription ID allocation */
    private subscriptionIdQueue: Promise<unknown> = Promise.resolve();

    /** Next message ID to assign for manager-generated RPC messages */
    private nextMessageId = 1;

    private emitter: EventEmitter;

    private decoder: (ws: WebSocket) => DataSyncDecoderFn;

    /**
     * Get the emitter for message events.
     * Used by HiveV2Socket instances to listen for messages.
     */
    public getEmitter(): EventEmitter {
        return this.emitter;
    }

    constructor(private getSocketURL: (contentId: string) => () => MaybePromise<URL>) {
        this.emitter = new EventEmitter();

        this.decoder = (ws: WebSocket) =>
            createGeneralDataSyncDecoder(async (message) => {
                // Handle reconnect RPC from the server: find the matching socket and reconnect it.
                if (
                    message.rpcRequest?.service === 'Connection' &&
                    message.rpcRequest?.method === 'reconnect'
                ) {
                    const subscriptionId = getSubscriptionId(message);
                    const data = this.websocketData.get(ws);
                    if (data && subscriptionId != null) {
                        const socket = [...data.sockets].find(
                            (s) => s.subscriptionId === subscriptionId
                        );
                        if (socket && !socket.isDestroyed()) {
                            await socket.reconnect();
                            return;
                        }
                    }
                }

                // Handle all message types with a single handler
                this.emitter.emit('message', { message, ws });

                // If the message carries a subscriptionId that no tracked V2Socket recognises,
                // signal the server so it can drop the orphaned connection.
                const subscriptionId = getSubscriptionId(message);
                if (subscriptionId != null) {
                    const data = this.websocketData.get(ws);
                    const hasMatch = data
                        ? [...data.sockets].some((s) => s.subscriptionId === subscriptionId)
                        : false;

                    if (!hasMatch) {
                        // Directly encode and send a reconnect RPC to the server so it can
                        // drop the orphaned subscription, without going through a HiveV2Socket.
                        const messageId = this.nextMessageId++;
                        const encoded = dataSyncProtocol.Message.encode(
                            dataSyncProtocol.Message.create({
                                rpcRequest: {
                                    id: messageId,
                                    service: 'Connection',
                                    method: 'reconnect',
                                    payload: dataSyncProtocol.ReconnectRequest.encode({
                                        subscriptionId,
                                    }).finish(),
                                    subscriptionId,
                                },
                            })
                        ).finish();
                        try {
                            ws.send(encoded);
                        } catch (_e) {
                            // WebSocket may not be open; ignore send failures here.
                        }
                    }
                }
            });
    }

    getSocketForContent(
        contentId: string,
        path: string,
        decoderHandlers: DataSyncDecoderHandlers,
        webSocketOptions?: WebSocketOptions
    ): HiveV2Socket {
        if (!this.websockets.has(contentId)) {
            const ws = new WebSocket(this.getSocketURL(contentId), {
                ...webSocketOptions,
                attemptReconnect: false,
            });
            this.websockets.set(contentId, ws);
            this.websocketData.set(ws, {
                contentId,
                sockets: new Set(),
                reconnectTimer: undefined,
                reconnectBackoffDelay: 1000,
                webSocketOptions,
            });
            this.setupWebSocketHandlers(ws);
        }
        const ws = this.websockets.get(contentId)!;
        const v2Socket: HiveV2Socket = new HiveV2Socket({
            path,
            webSocket: ws,
            forceReconnection: () => this.forceReconnectionForContent(contentId),
            decoderHandlers,
            managerEmitter: this.emitter,
            onRemove: () => {
                // Look up the current WebSocket at call time so that after replaceWebSocket()
                // migrates this socket to a new WebSocket, onRemove still closes the new one
                // rather than the stale original captured at socket-creation time.
                const currentWs = this.websockets.get(contentId);
                if (currentWs) {
                    this.maybeCloseWebSocket(currentWs);
                }
            },
            ensureWebSocketOpen: () => this.ensureWebSocketOpenForContent(contentId),
            getNewSubscriptionId: () => this.allocateSubscriptionId(),
            hasActiveSiblingPageSockets: () => {
                const data = this.websocketData.get(ws);
                if (!data) {
                    return false;
                }
                return [...data.sockets].some(
                    (s) =>
                        s !== v2Socket &&
                        !s.isDestroyed() &&
                        (s.state === WebSocketState.OPEN ||
                            s.state === WebSocketState.CONNECTING) &&
                        s.identifier.branchId === v2Socket.identifier.branchId &&
                        (s.identifier.type === dataSyncProtocol.ResourceType.PAGE ||
                            s.identifier.type === dataSyncProtocol.ResourceType.REUSABLE_CONTENT)
                );
            },
            restartBranchSocket: async () => {
                const data = this.websocketData.get(ws);
                if (!data) {
                    return;
                }
                // Find the branch socket for the same branchId that is not already open/connecting.
                const branchSocket = [...data.sockets].find(
                    (s) =>
                        s !== v2Socket &&
                        !s.isDestroyed() &&
                        s.identifier.type === dataSyncProtocol.ResourceType.BRANCH &&
                        s.identifier.branchId === v2Socket.identifier.branchId &&
                        s.state !== WebSocketState.OPEN &&
                        s.state !== WebSocketState.CONNECTING
                );
                if (branchSocket) {
                    await branchSocket.open();
                }
            },
        });

        // Track this HiveV2Socket
        this.websocketData.get(ws)!.sockets.add(v2Socket);

        return v2Socket;
    }

    /**
     * Allocate the next subscription ID. Calls are serialized so that no two
     * consumers ever receive the same ID even when awaited concurrently.
     */
    private allocateSubscriptionId(): Promise<number> {
        const result = this.subscriptionIdQueue.then(() => this.nextSubscriptionId++);
        this.subscriptionIdQueue = result;
        return result;
    }

    hasSocketForContent(contentId: string): boolean {
        return this.websockets.has(contentId);
    }

    /**
     * Ensure the WebSocket for the given contentId is opened.
     * If the WebSocket is in ERRORED state it is not destroyed, so we attempt to open it
     * directly — the util-websocket layer will handle reconnection internally. A WebSocket
     * should only be replaced entirely when it is destroyed.
     * This ensures opening is only triggered once per WebSocket, even if multiple
     * HiveV2Socket instances call this method simultaneously.
     */
    async ensureWebSocketOpenForContent(contentId: string): Promise<void> {
        // If there's already an ongoing open for this contentId, return that promise
        const existingPromise = this.openPromises.get(contentId);
        if (existingPromise) {
            return existingPromise;
        }

        const ws = this.websockets.get(contentId);
        if (!ws) {
            return;
        }

        // If already open, nothing to do
        if (ws.state === WebSocketState.OPEN) {
            return;
        }

        if (ws.isDestroyed()) {
            // If the WebSocket is destroyed, replace it with a fresh instance and open that.
            this.replaceWebSocket(contentId);
        }

        // Start opening and track the promise
        const openPromise = Promise.race([
            (async () => {
                // Re-read the current WebSocket from the map in case it was replaced externally
                // (e.g. when the WebSocket is destroyed and a fresh instance is created).
                const currentWs = this.websockets.get(contentId)!;
                try {
                    await currentWs.open();
                } catch (_e) {
                    // If opening fails, try to force the scheduled reconnection now.
                    // If there is no timer (e.g. on a freshly replaced WebSocket) this throws,
                    // which propagates out, causing scheduleReconnect's catch to schedule
                    // another retry automatically.
                    await currentWs.forceScheduledReconnection();
                }
            })(),
            new Promise<void>((_resolve, reject) => {
                setTimeout(() => reject(new Error('WebSocket open timeout')), 5000);
            }),
        ]).finally(() => {
            // Clean up the promise once it's done
            this.openPromises.delete(contentId);
        });

        this.openPromises.set(contentId, openPromise);
        return openPromise;
    }

    /**
     * Check if the shared WebSocket should be closed.
     * Called by HiveV2Socket when it's closed or destroyed.
     * If no tracked socket is still open or connecting, close the shared WebSocket.
     */
    private maybeCloseWebSocket(ws: WebSocket): void {
        const data = this.websocketData.get(ws);
        if (!data) {
            return;
        }

        const hasActiveSocket = [...data.sockets].some(
            (s) =>
                (s.state === WebSocketState.OPEN || s.state === WebSocketState.CONNECTING) &&
                !s.isDestroyed()
        );

        if (!hasActiveSocket) {
            // Cancel any pending auto-reconnect — this is an intentional close
            if (data.reconnectTimer) {
                clearTimeout(data.reconnectTimer);
                data.reconnectTimer = undefined;
            }

            // Close the WebSocket (stops reconnection via clearing the backoff timer)
            ws.close();

            // Clean up pending promises
            this.reconnectionPromises.delete(data.contentId);
            this.openPromises.delete(data.contentId);
        }
    }

    /**
     * Force a scheduled reconnection for the WebSocket associated with the given contentId.
     * This ensures reconnection is only triggered once per WebSocket, even if multiple
     * HiveV2Socket instances call this method.
     */
    async forceReconnectionForContent(contentId: string): Promise<void> {
        // If there's already an ongoing reconnection for this contentId, return that promise
        const existingPromise = this.reconnectionPromises.get(contentId);
        if (existingPromise) {
            return existingPromise;
        }

        const ws = this.websockets.get(contentId);
        if (!ws) {
            return;
        }

        // Start a new reconnection and track the promise
        const reconnectionPromise = ws.forceScheduledReconnection().finally(() => {
            // Clean up the promise once it's done
            this.reconnectionPromises.delete(contentId);
        });

        this.reconnectionPromises.set(contentId, reconnectionPromise);
        return reconnectionPromise;
    }

    /**
     * Force an immediate reconnection of every WebSocket that is currently waiting on
     * a scheduled (backoff) reconnection, bypassing the remaining delay.
     *
     * Only WebSockets with a pending auto-reconnect timer are affected: open/connecting
     * sockets have no timer, and intentionally-closed ones had their timer cleared by
     * {@link maybeCloseWebSocket}, so neither is touched. Safe to call at any time —
     * typically when the page regains focus after a disconnection.
     */
    reconnectAllNow(): void {
        for (const ws of this.websockets.values()) {
            const data = this.websocketData.get(ws);
            if (!data?.reconnectTimer) {
                continue;
            }

            // Cancel the pending backoff and reset the delay so this attempt — and any
            // automatic retry that follows it — starts from the shortest delay again.
            clearTimeout(data.reconnectTimer);
            data.reconnectTimer = undefined;
            data.reconnectBackoffDelay = 1000;

            this.ensureWebSocketOpenForContent(data.contentId).catch(() => {
                // If the immediate attempt fails, fall back to the normal scheduled retry.
                // The WebSocket may have been replaced since, so re-read it from the map.
                const currentWs = this.websockets.get(data.contentId);
                const currentData = currentWs ? this.websocketData.get(currentWs) : undefined;
                if (currentWs && currentData) {
                    this.scheduleReconnect(currentWs, currentData);
                }
            });
        }
    }

    /**
     * Setup handlers for a WebSocket to manage disconnect/reconnect timeouts.
     */
    private setupWebSocketHandlers(ws: WebSocket): void {
        ws.listen('state', (state: WebSocketState) => {
            if (state === WebSocketState.CLOSED) {
                this.handleWebSocketDisconnect(ws, state);
            } else if (state === WebSocketState.OPEN) {
                this.handleWebSocketReconnect(ws);
            } else if (state === WebSocketState.ERRORED) {
                // On error, schedule a reconnect attempt. We check active sockets NOW
                // because the manager's listener fires before the V2Socket listeners update
                // their own states, so sockets are still OPEN/CONNECTING here. The subsequent
                // CLOSED event (which always follows ERRORED) will find sockets already in
                // ERRORED state and be a no-op.
                this.handleWebSocketError(ws);
            }
        });

        const decoder = this.decoder(ws);

        ws.listen('message', (message: WebSocketMessage) => {
            if (typeof message === 'string') {
                return;
            }
            Promise.resolve(decoder(message)).catch((_error) => {
                // TODO: what should we do on decode error? For now, ignore.
            });
        });
    }

    /**
     * Handle when a WebSocket disconnects.
     * When the disconnection is an unexpected close (CLOSED, not ERRORED) and there are still
     * active V2Sockets, schedule an exponential-backoff reconnection attempt.
     */
    private handleWebSocketDisconnect(
        ws: WebSocket,
        state: WebSocketState.CLOSED | WebSocketState.ERRORED
    ): void {
        const data = this.websocketData.get(ws);
        if (!data) {
            return;
        }

        // Schedule an auto-reconnect only when:
        //  1. The close was unexpected (CLOSED), not a protocol/server error (ERRORED)
        //  2. At least one V2Socket is still active — if all were destroyed, maybeCloseWebSocket
        //     already cancelled reconnectTimer and called ws.close() intentionally
        if (state === WebSocketState.CLOSED) {
            const hasActiveSockets = [...data.sockets].some(
                (s) => s.state === WebSocketState.OPEN || s.state === WebSocketState.CONNECTING
            );

            if (hasActiveSockets) {
                this.scheduleReconnect(ws, data);
            }
        }
    }

    /**
     * Handle when a WebSocket enters the ERRORED state.
     * This is called before V2Socket listeners fire, so active sockets can still be detected.
     * Schedules a reconnect with exponential backoff. The subsequent CLOSED event
     * (which always follows ERRORED) will be a no-op because sockets will already be ERRORED.
     */
    private handleWebSocketError(ws: WebSocket): void {
        const data = this.websocketData.get(ws);
        if (!data) {
            return;
        }

        // Check sockets now — manager listener fires before V2Socket listeners so
        // sockets are still OPEN/CONNECTING at this point.
        const hasActiveSockets = [...data.sockets].some(
            (s) =>
                !s.isDestroyed() &&
                (s.state === WebSocketState.OPEN || s.state === WebSocketState.CONNECTING)
        );

        if (hasActiveSockets) {
            if (ws.isDestroyed()) {
                // If the WebSocket is destroyed, replace it with a fresh instance and open that.
                this.replaceWebSocket(data.contentId);
                return;
            }
            this.scheduleReconnect(ws, data);
        }
    }

    /**
     * Replace the shared WebSocket for a contentId with a fresh instance.
     * The new WebSocket gets a new URL (e.g. refreshed auth token) via the registered
     * getSocketURL callback. All tracked V2Sockets are migrated to the new WebSocket.
     */
    private replaceWebSocket(contentId: string): WebSocket {
        const oldWs = this.websockets.get(contentId)!;
        const oldData = this.websocketData.get(oldWs)!;

        // Cancel any pending reconnect timer on the old WebSocket.
        if (oldData.reconnectTimer) {
            clearTimeout(oldData.reconnectTimer);
            oldData.reconnectTimer = undefined;
        }

        const newWs = new WebSocket(this.getSocketURL(contentId), {
            ...oldData.webSocketOptions,
            attemptReconnect: false,
        });

        this.websockets.set(contentId, newWs);
        this.websocketData.set(newWs, {
            contentId,
            sockets: oldData.sockets, // same set of HiveV2Sockets
            reconnectTimer: undefined,
            reconnectBackoffDelay: 1000, // reset backoff for the fresh connection
            webSocketOptions: oldData.webSocketOptions,
        });

        this.setupWebSocketHandlers(newWs);

        // Migrate all tracked V2Sockets to the new WebSocket.
        for (const socket of oldData.sockets) {
            if (!socket.isDestroyed()) {
                socket.updateWebSocket(newWs);
            }
        }

        return newWs;
    }

    /**
     * Schedule an exponential-backoff reconnection attempt for a WebSocket.
     * Only starts a timer if one is not already pending.
     */
    private scheduleReconnect(ws: WebSocket, data: WebSocketData): void {
        // Only one reconnect timer at a time
        if (data.reconnectTimer) {
            return;
        }

        const delay = data.reconnectBackoffDelay;
        // Double the delay for the next attempt, capped at 30 s
        data.reconnectBackoffDelay = Math.min(data.reconnectBackoffDelay * 2, 30000);

        data.reconnectTimer = setTimeout(() => {
            data.reconnectTimer = undefined;
            this.ensureWebSocketOpenForContent(data.contentId).catch(() => {
                // If the attempt fails, schedule another retry (backoff already advanced).
                // Look up the current WebSocket from the map — it may have been replaced
                // since this timer was scheduled.
                const currentWs = this.websockets.get(data.contentId);
                const currentData = currentWs ? this.websocketData.get(currentWs) : undefined;
                if (currentData) {
                    this.scheduleReconnect(currentWs!, currentData);
                }
            });
        }, delay);
    }

    /**
     * Handle when a WebSocket reconnects.
     * Clear the disconnect timer, reset exponential backoff, and reopen all
     * non-destroyed V2Sockets associated with the WebSocket.
     */
    private handleWebSocketReconnect(ws: WebSocket): void {
        const data = this.websocketData.get(ws);
        if (!data) {
            return;
        }

        // Cancel any pending reconnect attempt and reset backoff to initial delay
        if (data.reconnectTimer) {
            clearTimeout(data.reconnectTimer);
            data.reconnectTimer = undefined;
        }
        data.reconnectBackoffDelay = 1000;

        // Reopen all V2Sockets that have not been destroyed and are not inactive so they can
        // re-subscribe. Inactive sockets were intentionally closed due to inactivity and must
        // not be resubscribed just because another socket on the same shared WebSocket reconnects.
        for (const socket of data.sockets) {
            if (!socket.isDestroyed() && !socket.inactive) {
                socket.open().catch((_error) => {
                    // Ignore errors — each socket manages its own retry logic.
                });
            }
        }
    }
}
